/**
 * Join state for screen tags. The stadium screen shows a QR whose code
 * rotates every two minutes; the fan page claims it once (claim_screen_join)
 * and remembers until when this device may write on that tag. Pure helpers:
 * no React, no Supabase.
 */
export interface ScreenJoin {
  validUntil: string;
}

export const JOIN_PARAM = 'j';
const storageKey = (tagId: string) => `idseat_screen_join_${tagId}`;

export function parseJoinParam(search: string): string | null {
  const raw = new URLSearchParams(search).get(JOIN_PARAM);
  const code = raw?.trim().toLowerCase() ?? '';
  return code.length > 0 ? code : null;
}

/** The same URL without the join code — the fan must not bookmark or share a code. */
export function stripJoinParam(url: string): string {
  const q = url.indexOf('?');
  if (q === -1) return url;
  const params = new URLSearchParams(url.slice(q + 1));
  params.delete(JOIN_PARAM);
  const rest = params.toString();
  return rest ? `${url.slice(0, q)}?${rest}` : url.slice(0, q);
}

export function readJoin(tagId: string): ScreenJoin | null {
  try {
    const raw = localStorage.getItem(storageKey(tagId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScreenJoin>;
    return typeof parsed.validUntil === 'string' ? { validUntil: parsed.validUntil } : null;
  } catch {
    return null;
  }
}

export function writeJoin(tagId: string, validUntil: string): void {
  try {
    localStorage.setItem(storageKey(tagId), JSON.stringify({ validUntil }));
  } catch {
    // storage blocked: the join still lives on the server for this device id
  }
}

export function clearJoin(tagId: string): void {
  try {
    localStorage.removeItem(storageKey(tagId));
  } catch {
    // nothing to clear
  }
}

export function isJoinValid(join: ScreenJoin | null, now: Date = new Date()): boolean {
  if (!join) return false;
  const until = Date.parse(join.validUntil);
  return Number.isFinite(until) && now.getTime() < until;
}

/** The insert guard (enforce_screen_join) refused the write: the join expired or never happened. */
export function isScreenJoinError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.includes('screen_join_required');
}
